import type { ReactNode } from 'react';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { constants } from '@clubmed/utils';
import { noop } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { proposalsSelector } from 'Store/booking/booking.selector';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { ctaActionTypes, PaidService } from 'Components/widgets/paid-service/PaidService';
import { asWidgetProps } from 'Src/types/widget-helpers';
import { Title } from 'Components/frameworks/title/Title';
import { AlternativePackageToolTip } from './AlternativePackageToolTip';
const {
  PACKAGES: { JOYVIEW_ROOM_ONLY, JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES },
} = constants;
const MAPPING_PACKAGE_INFOS = {
  [JOYVIEW_ALL_INCLUSIVE]: {
    color: COLORS.SAFFRON,
    icon: ICONS.ALL_INCLUSIVE,
    imageUrl: 'https://ns.clubmed.com/it/2019/be/joyview/Images.jpg',
    labelGA: 'AllInclusive',
    title: 'PAIBlockTitle',
  },
  [JOYVIEW_FOOD_AND_BEVERAGES]: {
    color: COLORS.ORANGE,
    icon: ICONS.DISHES,
    imageUrl: 'https://ns.clubmed.com/it/2019/be/joyview/FFB-JoyVIew.png',
    labelGA: 'FoodBeverage',
    title: 'FFBBlockTitle',
  },
};
type Props = {
  allProposals?: Array<Proposal>;
  isColumn?: boolean;
  selectedProposal: Proposal;
  selectProposal: (arg0: string) => void;
};

const getCtaType = (
  selectedProposal: Proposal,
  packageId: string,
  basePackageId: string,
): 'add' | 'included' | 'remove' => {
  if (!selectedProposal.alternativePackages || basePackageId === packageId) {
    return ctaActionTypes.included as 'add' | 'remove' | 'included';
  }

  if (selectedProposal.packageId === packageId) {
    return ctaActionTypes.remove as 'add' | 'remove' | 'included';
  }

  return ctaActionTypes.add as 'add' | 'remove' | 'included';
};

const findAlternativeProposalByPackage = (alternativePackages, packageId) => {
  return alternativePackages.find((bundle) => bundle.packageId === packageId);
};

export const AlternativePackages = ({
  allProposals,
  isColumn,
  selectedProposal,
  selectProposal,
}: Props): ReactNode => {
  const allProposalsFromStore = useSelector(proposalsSelector);

  if ((!allProposals || !allProposals.length) && !allProposalsFromStore.length) {
    throw new Error(
      'AlternativePackages should have allProposals props provided or the store should contains proposals',
    );
  }

  const basePackageId = (allProposals || allProposalsFromStore)[0].packageId;
  const { trackClickEvent } = useTrackEvent();
  const RONPackage = selectedProposal?.packages?.find(
    (selectedProposalPackage) => selectedProposalPackage?.id === JOYVIEW_ROOM_ONLY,
  );
  return selectedProposal.packages
    .filter((bundle): bundle is Package =>
      Boolean(
        bundle &&
          bundle.id !== JOYVIEW_ROOM_ONLY &&
          ((selectedProposal.alternativePackages &&
            findAlternativeProposalByPackage(selectedProposal.alternativePackages, bundle.id)) ||
            bundle.id === selectedProposal.packageId),
      ),
    )
    .map((otherPackage, index, mappedArray) => {
      const packageInfos = MAPPING_PACKAGE_INFOS[otherPackage.id];
      // if there is no alternativePackage that means that it's already selected and that we want to reset the package
      let alternativePackage;

      if (selectedProposal.alternativePackages) {
        alternativePackage =
          findAlternativeProposalByPackage(selectedProposal.alternativePackages, otherPackage.id) ||
          selectedProposal.alternativePackages[0];
      }

      const ctaType = getCtaType(selectedProposal, otherPackage.id, basePackageId);
      return (
        <Fragment key={otherPackage.id}>
          <Title className="mb-20 text-saffron" theme="title-sans-n4">
            {t(`booking.services.${packageInfos.title}`)}
          </Title>
          <PaidService
            addLabel={t('booking.common.add')}
            ctaType={ctaType}
            headColor={packageInfos.color}
            icon={packageInfos.icon}
            imageUrl={packageInfos.imageUrl}
            includedLabel={t('booking.common.included')}
            isColumn={isColumn}
            label={otherPackage.label}
            text={otherPackage.description}
            tooltip={asWidgetProps<React.ComponentProps<typeof PaidService>['tooltip']>({
              children: (
                <div className="text-initial flex w-full">
                  <AlternativePackageToolTip bundle={RONPackage} />
                  <AlternativePackageToolTip bundle={otherPackage} />
                </div>
              ),
              dataSelector: 'JoyviewUpsellTooltip',
              modalProps: {
                ariaLabel: otherPackage.label,
                closeLabel: t('common.close'),
              },
            })}
            onClick={() => {
              switch (ctaType) {
                case 'add':
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                    detail_click: 'add_joyview_packages',
                  });
                  break;

                case 'remove':
                  trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                    detail_click: 'remove_joyview_packages',
                  });
                  break;

                default:
                  noop();
                  break;
              }

              return selectProposal(alternativePackage?.proposalId);
            }}
            price={alternativePackage?.upsellPrice}
            removeLabel={t('booking.common.remove')}
          />
          {index < mappedArray.length - 1 && (
            <div className="mb-15 mt-20 text-center text-20 sm:mb-0 sm:mt-40">
              {t('booking.services.JVPackagesOr')}
            </div>
          )}
        </Fragment>
      );
    });
};
